import { useEffect, useRef, useState } from "react";

import { DEV_BYPASS_TOKEN } from "@/lib/contactApi";

/**
 * Cloudflare Turnstile widget (privacy-friendly, no-tracking bot check). Loads
 * the Turnstile script once and renders the widget explicitly. When no site key
 * is configured (local dev), a clearly-labelled manual fallback is shown so the
 * forms remain usable and testable — it never contacts Cloudflare.
 */

const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

interface TurnstileApi {
  render: (
    el: HTMLElement,
    options: {
      sitekey: string;
      callback: (token: string) => void;
      "expired-callback"?: () => void;
      "error-callback"?: () => void;
      theme?: "auto" | "light" | "dark";
    },
  ) => string;
  remove: (id: string) => void;
  reset: (id?: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

let scriptPromise: Promise<void> | null = null;

function loadScript(): Promise<void> {
  if (window.turnstile) return Promise.resolve();
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise<void>((resolve, reject) => {
    const script = document.createElement("script");
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Failed to load Turnstile"));
    document.head.appendChild(script);
  });
  return scriptPromise;
}

export interface TurnstileProps {
  onToken: (token: string) => void;
  onExpire?: () => void;
  className?: string;
}

export function Turnstile({ onToken, onExpire, className }: TurnstileProps): JSX.Element {
  const siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY;
  const containerRef = useRef<HTMLDivElement>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!siteKey || !containerRef.current) return;
    let widgetId: string | undefined;
    const el = containerRef.current;
    let cancelled = false;

    loadScript()
      .then(() => {
        if (cancelled || !window.turnstile) return;
        widgetId = window.turnstile.render(el, {
          sitekey: siteKey,
          callback: onToken,
          "expired-callback": onExpire,
          "error-callback": () => setFailed(true),
          theme: "auto",
        });
      })
      .catch(() => setFailed(true));

    return () => {
      cancelled = true;
      if (widgetId && window.turnstile) window.turnstile.remove(widgetId);
    };
  }, [siteKey, onToken, onExpire]);

  if (!siteKey) {
    return (
      <div className={className}>
        <button
          type="button"
          onClick={() => onToken(DEV_BYPASS_TOKEN)}
          className="rounded-md border border-dashed px-3 py-2 text-sm text-muted-foreground hover:bg-accent"
        >
          Simulate verification (dev — no Turnstile key configured)
        </button>
      </div>
    );
  }

  if (failed) {
    return (
      <p className={`text-sm text-destructive ${className ?? ""}`}>
        The verification widget failed to load. Please email us directly.
      </p>
    );
  }

  return <div ref={containerRef} className={className} />;
}
